import { CartLineEdgeNode, SelectedOption } from 'shopify-buy';

import { LineItemPrice, toCents } from './LineItemPrice';
import { Quantity } from './Quantity';
import React from 'preact/compat';
import { TrashIcon } from './icons/TrashIcon';
import { removeItem } from './Cart';
import { toShopifyNumericId } from '../storefront-cart';

function isDefaultOption(option: SelectedOption): boolean {
  return (
    option.name.toLowerCase() === 'title' &&
    option.value.toLowerCase() === 'default title'
  );
}

function isPrivateAttribute(attribute: string): boolean {
  return attribute.startsWith('_');
}

type AnyFunction = (...args: any[]) => any;

export const LineItem: React.FC<{
  item: CartLineEdgeNode;
  onChange: () => void;
}> = ({
  item,
  onChange,
}: {
  item: CartLineEdgeNode;
  onChange: AnyFunction;
}) => {
  return (
    <div className="cart-drawer__item" role={'group'} aria-label={'Product'}>
      <div className="cart-drawer__item__image">
        {item.merchandise?.image && (
          <a
            href={`${process.env.STOREFRONT_API_STORE_URL}/products/${item.merchandise?.product.handle}`}
          >
            <img
              src={item.merchandise?.image.src}
              alt={item.merchandise.product.title}
            />
          </a>
        )}
      </div>

      <div className="cart-drawer__item-details">
        <div className="cart-drawer__item-details-top">
          <a
            href={`${process.env.STOREFRONT_API_STORE_URL}/products/${
              item.merchandise?.product?.handle
            }?variant=${toShopifyNumericId(item.merchandise?.id)}`}
          >
            <h6 className={'cart-drawer__item__title'}>
              {item.merchandise?.product?.title}
            </h6>
            <ul className="cart-drawer__item__options">
              {item.merchandise?.selectedOptions &&
                item.merchandise.selectedOptions.map((option) => {
                  if (isDefaultOption(option)) {
                    return;
                  }

                  return (
                    <li className={'line-item-option'}>
                      <span className={'line-item-option__name'}>
                        {option.name}
                      </span>
                      <span className={'line-item-option__spacer'}>:</span>
                      <span className={'line-item-option__value'}>
                        {option.value}
                      </span>
                    </li>
                  );
                })}
            </ul>
            <ul className="cart-drawer__item__options cart-drawer__item__options--attributes">
              {item.attributes &&
                item.attributes.map((attr) => {
                  if (!isPrivateAttribute(attr.key)) {
                    return (
                      <li className={'line-item-option'}>
                        <span className={'line-item-option__name'}>
                          {attr.key}
                        </span>
                        <span className={'line-item-option__spacer'}>:</span>
                        <span className={'line-item-option__value'}>
                          {attr.value}
                        </span>
                      </li>
                    );
                  }
                  return null;
                })}
            </ul>
            {item.sellingPlanAllocation && (
              <ul className="cart-drawer__item__options cart-drawer__item__options--selling-plan">
                <li className={'line-item-option'}>
                  {item.sellingPlanAllocation.sellingPlan.name}
                </li>
              </ul>
            )}
            {item.__typename === 'ComponentizableCartLine' &&
              Array.isArray(item.lineComponents) && (
                <ul className="cart-drawer__item__options cart-drawer__item__options--components">
                  {item.lineComponents
                    .reverse()
                    .map((component: Partial<CartLineEdgeNode>) => {
                      return (
                        <li className={'line-item-option'} key={component.id}>
                          <div
                            className="cart-drawer__item__component"
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '40px auto',
                              gap: '8px',
                              marginBottom: '8px',
                            }}
                          >
                            <div
                              className="cart-drawer__item__component-image"
                              style={{
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                width: '40px',
                                height: '40px',
                                overflow: 'hidden',
                              }}
                            >
                              {component.merchandise?.image && (
                                <img
                                  style={{ maxWidth: '40px' }}
                                  src={component.merchandise?.image.src}
                                  alt={component.merchandise.product.title}
                                />
                              )}
                            </div>
                            <div className={'line-item-option__name'}>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                }}
                              >
                                <div className={'quantity-title-price'}>
                                  <span>
                                    {component.merchandise?.product.title}
                                  </span>
                                  <span>
                                    <span style={{ width: '25px' }}>
                                      x{component.quantity}
                                    </span>
                                  </span>
                                  <span>
                                    (
                                    <>
                                      {window.Shopify.formatMoney(
                                        toCents(
                                          component?.cost?.totalAmount
                                            ?.amount || 0
                                        )
                                      )}
                                    </>
                                    )
                                  </span>
                                </div>
                                <div>
                                  <small style={{ fontSize: '14px' }}>
                                    {!isDefaultOption({
                                      name: 'title',
                                      value: component.merchandise?.title || '',
                                    }) ? (
                                      <span>
                                        {component.merchandise?.title}
                                      </span>
                                    ) : (
                                      <span>&nbsp;</span>
                                    )}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                </ul>
              )}
          </a>
          <div className="cart-drawer__item__remove">
            <button
              onClick={() => removeItem(item.id).then(onChange)}
              aria-label={`Remove ${item?.merchandise.product?.title} from cart`}
            >
              <TrashIcon
                title={`remove ${item?.merchandise.product?.title} from your cart`}
              />
            </button>
          </div>
        </div>
        <div className="cart-drawer__item-details-bottom">
          <div className="cart-drawer__item__quantity">
            <Quantity line={item} onChange={onChange} />
          </div>
          <div className="cart-drawer__item__price">
            <span className="price regular text-disabled will-load">
              <LineItemPrice line={item} type={'compareAt'} />
            </span>

            <span className="price discounted will-load">
              <LineItemPrice line={item} type={'regular'} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
